import type {
  ProfileTransformResponse,
  ABOInfo,
  PartyInfo,
  WhatsApp,
  AdditionalRegInfo,
} from "../Profile/profileAPI.types";
import type {
  AboProfileResponse,
  AboProfileParty,
  AboProfileAddress,
  AboProfilePhoneNumber,
  AboProfileData,
} from "./AboProfileAPI.types";

/**
 * Convert a YYYYMM integer (e.g. 202501) into a date string "YYYY-MM"
 * used for display in pin/award date fields.
 * Returns empty string when the input is falsy or zero.
 */
const qualPeriodToDateString = (qualPeriod: number | undefined): string => {
  if (!qualPeriod) return "";
  const str = String(qualPeriod);
  if (str.length !== 6) return "";
  return `${str.substring(0, 4)}-${str.substring(4, 6)}`;
};

/**
 * Reassemble discrete addressLine1–4 fields into the addressLines[] array
 * expected by the existing component contract.
 */
const buildAddressLines = (addr: AboProfileAddress): string[] => {
  return [addr.addressLine1, addr.addressLine2, addr.addressLine3, addr.addressLine4].filter((line): line is string =>
    Boolean(line),
  );
};

const transformAddress = (addr: AboProfileAddress | undefined) => {
  if (!addr) return undefined;
  return {
    type: addr.addressType ?? "",
    addressLines: buildAddressLines(addr),
    municipality: addr.city ?? "",
    region: addr.stateName || addr.state || "",
    postalCode: addr.postalCd ?? "",
    country: addr.country ?? "",
  };
};

const transformAddresses = (addr: AboProfileAddress | undefined) => {
  const result = transformAddress(addr);
  return result ? [result] : [];
};

const transformPhoneNumber = (p: AboProfilePhoneNumber | undefined) => {
  if (!p) return undefined;
  return {
    displayNumber: p.displayNumber ?? p.phoneNumber ?? "",
    extension: p.phoneCntryCd ?? "",
    number: p.phoneNumber ?? "",
    outOfCountryCallingNumber: p.phoneCntryCd ?? "",
  };
};

const transformPhoneNumbers = (phone: AboProfilePhoneNumber | undefined) => {
  const result = transformPhoneNumber(phone);
  return result ? [result] : [];
};

/**
 * Convert a whatsapp string into the WhatsApp[] array expected by existing types.
 * The REST API returns whatsapp as a plain string; only ecommAddr was ever used.
 */
const whatsappStringToArray = (whatsapp: string | undefined): WhatsApp[] => {
  if (!whatsapp) return [];
  return [
    {
      contactId: "",
      contactPointTypeCd: "",
      globalPartyId: "",
      localPartyId: "",
      partyId: "",
      usageList: [],
      allowForLogin: false,
      ecommAddr: whatsapp,
      ecommName: "",
      ecommTypeCd: "",
      ecommTypeName: "",
      statusCd: "",
    },
  ];
};

/**
 * Map a single REST party object into the existing PartyInfo shape.
 */
const transformParty = (party: AboProfileParty): PartyInfo => ({
  partyId: party.partyId ?? "",
  localeDisplayName: party.nameLocal ?? "",
  displayName: party.name ?? "",
  localeName: {
    givenName: "",
    familyName: "",
  },
  latinName: {
    givenName: "",
    familyName: "",
  },
  isPrimary: party.isPrimary ?? false,
  gender: party.gender ?? "",
  isoBirthdate: party.birthDate ?? "",
  preferredName: "",
  preferredLanguage: party.preferredLanguage ?? "",
  preferredLanguageCode: party.preferredLanguage ?? "",
  phoneNumbers: transformPhoneNumbers(party.phone),
  emailAddresses: party.email ? [{ email: party.email, type: "PersonalEmail" }] : [],
  addresses: transformAddresses(party.address),
  whatsApp: whatsappStringToArray(party.whatsapp),
  imageRefUrl: party.imageRefUrl ?? "",
});

/**
 * For non-MDMS ("magic") markets the parties array is empty.
 * Synthesize a single primary party from top-level profile fields
 * so that downstream components always have at least one party to render.
 */
const synthesizePrimaryParty = (p: AboProfileData): PartyInfo => {
  const primaryAddr = p.address;
  const primaryPhone = p.phone;

  return {
    partyId: "",
    localeDisplayName: p.nameLocal ?? "",
    displayName: p.name ?? "",
    localeName: { givenName: "", familyName: "" },
    latinName: { givenName: "", familyName: "" },
    isPrimary: true,
    gender: p.gender ?? "",
    isoBirthdate: p.birthDate ?? "",
    preferredName: "",
    preferredLanguage: p.isoLanguage ?? "",
    preferredLanguageCode: p.isoLanguage ?? "",
    phoneNumbers: transformPhoneNumbers(primaryPhone),
    emailAddresses: p.email ? [{ email: p.email, type: "PersonalEmail" }] : [],
    addresses: transformAddresses(primaryAddr),
    whatsApp: whatsappStringToArray(p.whatsapp),
  };
};

/**
 * Main transform: maps the REST response into the existing
 * ProfileTransformResponse shape consumed by all ABOProfile slider components.
 */
export const transformAboProfileResponse = (response: AboProfileResponse): ProfileTransformResponse => {
  if (!response?.profile) return {};

  const { profile } = response;

  // --- aboInfo ---
  const aboInfo: ABOInfo = {
    affNo: response.aff ?? "",
    aboNo: response.abo ?? "",
    name: profile.name ?? "",
    localName: profile.nameLocal ?? "",
    displayName: profile.displayName,
    isoLanguageCode: profile.isoLanguage ?? "",
    privacyFlag: profile.privacyFlag ?? false,
    imageRefUrl: profile.imageRefUrl,
    isNew: false,
    isNewInCurrentMonth: false,
    isNewMigrated: false,
    entryDate: profile.entryDate ?? "",
    joinedDate: "",
    countryCode: profile.isoCountry ?? "",
    verifiedCustomerFlag: profile.verifiedCustomerFlag ?? false,
    businessNatureCode: profile.busNature ?? 0,
    isoBirthdate: profile.birthDate ?? "",
    renewalDate: profile.expireDate || "",
    registrationChannelCd: "",
    status: profile.statusName ?? profile.status ?? "",
    recentSponsorDate: profile.recentSponsorDate ?? "",
    currentBusinessNatureEffectiveDate: profile.currentBusinessNatureEffectiveDate || "--",
    sponsor: profile.sponsor
      ? {
          affNo: profile.sponsor.affNo,
          aboNo: profile.sponsor.aboNo,
          isValid: profile.sponsor.isValid,
          isPlatinum: profile.sponsor.isPlatinum,
          name: profile.sponsor.name,
          nameLocal: profile.sponsor.nameLocal,
          displayName: profile.sponsor.displayName,
          privacyFlag: profile.sponsor.privacyFlag,
          email: profile.sponsor.email,
          phone: transformPhoneNumber(profile.sponsor.phone),
          address: transformAddress(profile.sponsor.address),
          imageRefUrl: profile.sponsor.imageRefUrl,
        }
      : { affNo: "", aboNo: "", isValid: false, isPlatinum: false },
    platinumSponsor: profile.platinumSponsor
      ? {
          affNo: profile.platinumSponsor.affNo,
          aboNo: profile.platinumSponsor.aboNo,
          isValid: profile.platinumSponsor.isValid,
          name: profile.platinumSponsor.name,
          nameLocal: profile.platinumSponsor.nameLocal,
          displayName: profile.platinumSponsor.displayName,
          privacyFlag: profile.platinumSponsor.privacyFlag,
          email: profile.platinumSponsor.email,
          phone: transformPhoneNumber(profile.platinumSponsor.phone),
          address: transformAddress(profile.platinumSponsor.address),
          imageRefUrl: profile.platinumSponsor.imageRefUrl,
        }
      : { affNo: "", aboNo: "", isValid: false },
    fosterSponsor: profile.fosterSponsor
      ? {
          affNo: profile.fosterSponsor.affNo,
          aboNo: profile.fosterSponsor.aboNo,
          isValid: profile.fosterSponsor.isValid,
          isPlatinum: profile.fosterSponsor.isPlatinum,
          name: profile.fosterSponsor.name,
          nameLocal: profile.fosterSponsor.nameLocal,
          displayName: profile.fosterSponsor.displayName,
          privacyFlag: profile.fosterSponsor.privacyFlag,
          email: profile.fosterSponsor.email,
          phone: transformPhoneNumber(profile.fosterSponsor.phone),
          address: transformAddress(profile.fosterSponsor.address),
          imageRefUrl: profile.fosterSponsor.imageRefUrl,
        }
      : { affNo: "", aboNo: "", isValid: false, isPlatinum: false },
    internationalSponsor: profile.internationalSponsor
      ? {
          affNo: profile.internationalSponsor.affNo,
          aboNo: profile.internationalSponsor.aboNo,
          isValid: profile.internationalSponsor.isValid,
          name: profile.internationalSponsor.name,
          nameLocal: profile.internationalSponsor.nameLocal,
          displayName: profile.internationalSponsor.displayName,
          privacyFlag: profile.internationalSponsor.privacyFlag,
          email: profile.internationalSponsor.email,
          phone: transformPhoneNumber(profile.internationalSponsor.phone),
          address: transformAddress(profile.internationalSponsor.address),
          imageRefUrl: profile.internationalSponsor.imageRefUrl,
        }
      : { affNo: "", aboNo: "", isValid: false },
    whatsApp: whatsappStringToArray(profile.whatsapp),
  };

  // --- awardInfo (pick higher rank between hatAwards and garAwards for each slot) ---
  const hat = profile.hatAwards;
  const gar = profile.garAwards;

  const hatCurrentRank = hat?.current?.code ?? 0;
  const garCurrentRank = gar?.current?.code ?? 0;
  const currentAward = garCurrentRank > hatCurrentRank ? gar?.current : hat?.current;
  const currentRank = Math.max(hatCurrentRank, garCurrentRank);

  const hatHighestRank = hat?.highest?.code ?? 0;
  const garHighestRank = gar?.highest?.code ?? 0;
  const highestAward = garHighestRank > hatHighestRank ? gar?.highest : hat?.highest;
  const highestRank = Math.max(hatHighestRank, garHighestRank);

  const hatTrackingRank = hat?.tracking?.code ?? 0;
  const garTrackingRank = gar?.tracking?.code ?? 0;
  const trackingAward = garTrackingRank > hatTrackingRank ? gar?.tracking : hat?.tracking;
  const trackingRank = Math.max(hatTrackingRank, garTrackingRank);

  const awardInfo = {
    currentPin: currentRank,
    currentPinDate: qualPeriodToDateString(currentAward?.qualPeriod),
    currentPinLevel: currentAward?.name ?? "",
    highestAwardAchieved: highestRank,
    highestAwardAchievedDate: qualPeriodToDateString(highestAward?.qualPeriod),
    highestAwardAchievedLevel: highestAward?.name ?? "",
    highestTrackingAward: trackingRank,
    highestTrackingAwardLevel: trackingAward?.name ?? "",
    highestTrackingAwardDate: qualPeriodToDateString(trackingAward?.qualPeriod),
  };

  // --- partyInfo ---
  const hasParties = Array.isArray(profile.parties) && profile.parties.length > 0;
  const partyInfo: PartyInfo[] = hasParties
    ? (profile.parties ?? [])
        .map((party) => transformParty(party))
        .sort((a, b) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        })
    : [synthesizePrimaryParty(profile)];

  // --- additionalRegInfo ---
  const additionalRegInfo: AdditionalRegInfo = {
    autoRenewal: profile.autoRenewal ?? false,
    orderBlock: profile.orderBlock ?? false,
    sponsorBlock: profile.sponsorBlock ?? false,
    passport: false,
    contractSigned: profile.contractSigned ?? false,
    isRegisteredCustomerPlus: false,
  };

  if (profile.renewalConsent !== undefined) {
    additionalRegInfo.renewalConsent = profile.renewalConsent;
  }

  // --- orderInfo ---
  const orderInfo = {
    aboRecentOrderDate: profile.recentOrderDate ?? "",
  };

  return {
    aboInfo,
    awardInfo,
    partyInfo,
    additionalRegInfo,
    orderInfo,
    isPlatinumOrAbove: profile.isPlatinumOrAbove ?? false,
    isEmeraldAndAbove: profile.isEmeraldAndAbove ?? false,
    isDiamondAndAbove: profile.isDiamondAndAbove ?? false,
    pinAwardSummary: profile.awardInfo,
  };
};
